import { trace } from "@nopeer";
import { NineUxUtil } from "./dialog/NineUxUtil.js";

class NineSplitter extends HTMLElement {

	#mode;

	constructor() {
		super();
		this.attachShadow({ mode: "open" });
	}

	connectedCallback() {

		this.#init();
	}

	#isSiblingOnly = () => {
		const attr = this.getAttribute("only-sibling");

		// 1. 속성 자체가 없으면 false
		if (attr === null) return false;

		// 2. 속성이 존재하되 "false" 문자열이면 false
		return (attr.toLowerCase() === "false") ? false : true;
	};

	#detectMode = (el) => {
		const prev = el.previousElementSibling;
		const next = el.nextElementSibling;
		if (!prev || !next) {
			this.#mode = this.classList.contains('h') ? "h" : "v";
			return;
		}

		const prevRect = prev.getBoundingClientRect();
		const nextRect = next.getBoundingClientRect();

		if (this.classList.contains('h')) {
			this.#mode = "h";
		} else if (this.classList.contains('v')) {
			this.#mode = "v";
		} else {
			this.#mode = (Math.abs(prevRect.top - nextRect.top) < 5) ? "h" : "v";
		}
	};

	#startDrag = (e) => {

		e.preventDefault();
		e.stopPropagation();

		const splitterRect = this.getBoundingClientRect();
		const isHorizontal = this.#mode === "h";

		// 마우스 포인터와 스플리터 시작점 사이의 거리
		const clickOffset = isHorizontal
			? e.clientX - splitterRect.left
			: e.clientY - splitterRect.top;

		const dragBar = document.createElement("div");
		dragBar.className = `nx-splitter-drag-bar-${this.#mode}`;

		Object.assign(dragBar.style, {
			position: "absolute",
			zIndex: "999",
			background: "#666",
			opacity: "0.6",
			pointerEvents: "none"
		});

		const root = this.getRootNode({ composed: true });
		const parent = root instanceof ShadowRoot ? root.host : this.parentElement;
		const prev = this.previousElementSibling;
		const next = this.nextElementSibling;

		if (!parent || !prev || !next) {
			trace.warn("Spliter's parent or siblings not found.");
			return;
		}

		(parent.shadowRoot || parent).appendChild(dragBar);
		const dragBarOffsetParentRect = dragBar.offsetParent.getBoundingClientRect();

		const prevRect = prev.getBoundingClientRect();
		const nextRect = next.getBoundingClientRect();

		// 드래그 바의 초기 위치와 크기 설정
		const initialSplitterPosInParent = (isHorizontal
			? splitterRect.left - dragBarOffsetParentRect.left
			: splitterRect.top - dragBarOffsetParentRect.top) + clickOffset;

		if (isHorizontal) {
			dragBar.style.top = "0";
			dragBar.style.left = `${initialSplitterPosInParent}px`;
			dragBar.style.width = "2px";
			dragBar.style.height = "100%";
		} else {
			dragBar.style.left = "0";
			dragBar.style.top = `${initialSplitterPosInParent}px`;
			dragBar.style.height = "2px";
			dragBar.style.width = "100%";
		}

		dragBar.style.mixBlendMode = "difference";
		dragBar.style.zIndex = "99999";

		const minLimit = isHorizontal
			? prevRect.left - dragBarOffsetParentRect.left
			: prevRect.top - dragBarOffsetParentRect.top;
		const maxLimit = isHorizontal
			? nextRect.right - dragBarOffsetParentRect.left - splitterRect.width
			: nextRect.bottom - dragBarOffsetParentRect.top - splitterRect.height;

		const onMove = moveEvent => {
			const clientPos = isHorizontal ? moveEvent.clientX : moveEvent.clientY;
			const currentPosInParent = isHorizontal
				? clientPos - dragBarOffsetParentRect.left
				: clientPos - dragBarOffsetParentRect.top;

			const clampedPos = Math.max(minLimit, Math.min(currentPosInParent, maxLimit));

			if (isHorizontal) {
				dragBar.style.left = `${clampedPos}px`;
			} else {
				dragBar.style.top = `${clampedPos}px`;
			}
		};

		const onUp = () => {

			window.removeEventListener("mousemove", onMove);
			window.removeEventListener("mouseup", onUp);
			dragBar.remove();

			const allChildren = Array.from(parent.children);
			const allPanels = allChildren.filter(el => el.tagName.toLowerCase() !== 'nx-splitter');

			// ⭐⭐ gap 크기 계산 ⭐⭐
			const style = window.getComputedStyle(parent);
			const gapValue = isHorizontal ? style.getPropertyValue('column-gap') : style.getPropertyValue('row-gap');
			const gapSize = parseFloat(gapValue) || 0;
			const gapCount = allChildren.length > 1 ? allChildren.length - 1 : 0;
			const totalGapSize = gapCount * gapSize;

			const finalDragBarPos = isHorizontal ? parseFloat(dragBar.style.left) : parseFloat(dragBar.style.top);
			const dragOffset = finalDragBarPos - initialSplitterPosInParent;

			const prevSize = isHorizontal ? prev.getBoundingClientRect().width : prev.getBoundingClientRect().height;
			const nextSize = isHorizontal ? next.getBoundingClientRect().width : next.getBoundingClientRect().height;

			let newPrevSize = prevSize + dragOffset;
			let newNextSize = nextSize - dragOffset;

			// 패널 크기가 음수가 되지 않도록 제한하고, 다른 패널에 차이를 보정
			if (newPrevSize < 0) {
				newNextSize += newPrevSize;
				newPrevSize = 0;
			}
			if (newNextSize < 0) {
				newPrevSize += newNextSize;
				newNextSize = 0;
			}

			const initialSizes = allPanels.map(panel => isHorizontal ? panel.getBoundingClientRect().width : panel.getBoundingClientRect().height);

			const totalSplitterSize = allChildren.reduce((sum, child) => {
				if (child.tagName.toLowerCase() === 'nx-splitter') {
					return sum + (isHorizontal ? child.getBoundingClientRect().width : child.getBoundingClientRect().height);
				}
				return sum;
			}, 0);

			const totalContainerSize = (isHorizontal ? parent.getBoundingClientRect().width : parent.getBoundingClientRect().height);
			const totalFlexSpace = totalContainerSize - totalSplitterSize - totalGapSize;

			// --- 원인 파악을 위한 로그 추가 ---
			trace.log(`[Debug] ---------------------------`);
			trace.log(`[Debug] TotalContainerSize: ${totalContainerSize}`);
			trace.log(`[Debug] TotalSplitterSize: ${totalSplitterSize}`);
			trace.log(`[Debug] GapSize: ${gapSize}, GapCount: ${gapCount}`);
			trace.log(`[Debug] PrevSize(Old): ${prevSize}, NextSize(Old): ${nextSize}`);
			trace.log(`[Debug] DragOffset: ${dragOffset}`);
			trace.log(`[Debug] NewPrevSize: ${newPrevSize}, NewNextSize: ${newNextSize}`);

			allPanels.forEach((panel, index) => {
				const isChartPanel = panel.querySelector('canvas') !== null;
				const currentSize = isHorizontal ? panel.getBoundingClientRect().width : panel.getBoundingClientRect().height;
				trace.log(`[Debug] Panel[${index}] (${isChartPanel ? 'Canvas' : 'Normal'}): Current=${currentSize.toFixed(2)}, New=${(panel === prev ? newPrevSize : (panel === next ? newNextSize : initialSizes[index])).toFixed(2)}`);
			});
			trace.log(`[Debug] ---------------------------`);

			if (this.#isSiblingOnly()) {
				const prev = this.previousElementSibling;
				const next = this.nextElementSibling;

				if (prev && next) {
					// prev와 next의 크기만 조정
					prev.style.flex = `0 0 ${newPrevSize}px`;
					next.style.flex = `0 0 ${newNextSize}px`;
				}
			} else {
				let flexSum = 0;
				allPanels.forEach((panel, index) => {
					let newSize;
					if (panel === prev) {
						newSize = newPrevSize;
					} else if (panel === next) {
						newSize = newNextSize;
					} else {
						newSize = initialSizes[index];
					}

					const newFlexBasis = newSize / totalFlexSpace;
					//panel.style.flex = `${newFlexBasis} ${newFlexBasis} 0`;

					trace.log(panel);

					if (panel.classList.contains('sidebar')) {
						// 드래그가 끝난 시점의 newSize(px)를 그대로 고정값으로 할당
						// 이렇게 하면 비율(%)이 아니라 딱 그 픽셀만큼만 자리를 차지하게 됩니다.
						panel.style.flex = `0 0 ${newSize}px`;
					} else {
						// 나머지 메인 보드 같은 유연한 패널은 비율(%)로 처리
						//const newFlexBasis = newSize / totalFlexSpace;
						panel.style.flex = `${newFlexBasis} ${newFlexBasis} 0`;
					}

					flexSum += newFlexBasis;
				});
			}

			//trace.log(`dragOffset: ${dragOffset}`);
			//trace.log(`Calculated FlexSum: ${flexSum}`);
		};

		window.addEventListener("mousemove", onMove);
		window.addEventListener("mouseup", onUp);
	};

	get cssPath() {
		return this.getAttribute("css-path") || NineUxUtil.cssPath;
	}

	#init = () => {
		this.#detectMode(this);
		this.classList.add(this.#mode);

		const contents = this.innerHTML.trim();
		//const gripClass = `grip-${this.#mode}`;
		const gripTmpl = (contents === "") ? `<div class="grip"></div>` : `<div class="grip"></div><div class="inner-container">${contents}</div><div class="grip"></div>`;

		const customImport = nine.cssPath ? `@import "${nine.cssPath}/nine-util.css";` : "";

		this.innerHTML = "";
		const htmlTmpl = document.createElement("template");

		htmlTmpl.innerHTML = `
			<style>
				@import "https://cdn.jsdelivr.net/npm/@ninebone/util@${__APP_VERSION__}/dist/css/nine-util.css";
				${customImport}
			</style>
			${gripTmpl}
        `;

		this.shadowRoot.appendChild(htmlTmpl.content.cloneNode(true));

		this.shadowRoot.querySelectorAll(".grip").forEach(el => {
			el.addEventListener("mousedown", e => this.#startDrag(e));
		});

		this.#prepareLayout();

		window.addEventListener("resize", () => this.#prepareLayout());
	};

	#prepareLayout = () => {

		const isHorizontal = this.#mode === "h";
		const parent = this.parentElement;
		const allPanels = Array.from(parent.children).filter(el => el.tagName.toLowerCase() !== 'nine-splitter');
		if (allPanels.length < 2) return;

		const parentRect = parent.getBoundingClientRect();
		const totalContentSize = allPanels.reduce((sum, el) => {
			const size = isHorizontal ? el.getBoundingClientRect().width : el.getBoundingClientRect().height;
			return sum + size;
		}, 0);
		const totalParentSize = isHorizontal ? parentRect.width : parentRect.height;

		allPanels.forEach(panel => {
			// 🎯 이미 flex 인라인 스타일이 세팅되어 있다면 리사이징 폭거를 방지하기 위해 건너뜀
			if (panel.style.flex && panel.style.flex.trim() !== '') {
				return;
			}
			
			const size = isHorizontal ? panel.getBoundingClientRect().width : panel.getBoundingClientRect().height;
			const newSize = totalParentSize * (size / totalContentSize);
			const flexGrow = newSize / totalParentSize;
			panel.style.flex = `${flexGrow} ${flexGrow} 0`;

			trace.log(panel.style.flex);
		});
	};
}

if (!customElements.get('nine-splitter')) {
	customElements.define("nine-splitter", NineSplitter);
}